/**
 * Trägerart (ownership) codes accepted by the `traeger` query parameter of the
 * clinic-registry search endpoint (`GET /clinics/search`). These single-letter
 * codes are distinct from the legacy {@link AysClinicType} value type.
 */
export type AysClinicRegistryTraeger = 'f' | 'ö' | 'p';

/** All Trägerart codes in display order. */
export const AYS_CLINIC_REGISTRY_TRAEGER: AysClinicRegistryTraeger[] = [
  'f',
  'ö',
  'p',
];

/** Human-readable German labels for each Trägerart code. */
export const AYS_CLINIC_REGISTRY_TRAEGER_LABELS: Record<
  AysClinicRegistryTraeger,
  string
> = {
  f: 'Freigemeinnützig',
  ö: 'Öffentlich',
  p: 'Privat',
};

/**
 * Shortest free-text query worth running a clinic search for.
 *
 * A single character matches thousands of clinics (`q=Klinik` alone matches
 * >6000) while only one page is ever fetched, so a 1-char search returns an
 * arbitrary slice that looks like a complete result.
 *
 * This lives in the domain because it is one rule, not three: it has to hold
 * wherever a clinic search starts — the analytics pickers, the clinic-browser
 * autocomplete, and the `?q=` guard on the search page, which is deep-linkable
 * and so reachable without ever passing through an input. Those three each
 * carried their own `2`; a search accepted by one and refused by another is
 * incoherent from the user's side, and the copies had already started drifting
 * apart in name.
 */
export const AYS_CLINIC_SEARCH_MIN_QUERY_LENGTH = 2;

/**
 * Optional criteria that narrow a clinic-registry search in addition to the
 * free-text query. Every field is optional; an empty object performs an
 * unfiltered search.
 */
export interface AysClinicSearchCriteria {
  /** Bundesland code (matches {@link BdState} ids, e.g. 11 = Berlin). */
  bundesland?: number | null;
  /** Trägerart code (f/ö/p). */
  traeger?: AysClinicRegistryTraeger | null;
  /**
   * Report years to restrict to (repeatable); the endpoint collapses to the
   * MAX matching year per clinic.
   */
  years?: number[];
  /**
   * Aktiv-Filter — tri-state. `null`/`undefined` leaves the search unrestricted
   * (active AND inactive clinics). `true` keeps only clinics that are currently
   * live; `false` keeps only inactive ones. The endpoint treats the omitted
   * parameter as "all", so an unset value must NOT send the query parameter at
   * all.
   */
  onlyLive?: boolean | null;
  /**
   * Qualitätsbericht filter — tri-state. `null`/`undefined` leaves the search
   * unrestricted (clinics with AND without a report). `true` keeps only clinics
   * that have published a Qualitätsbericht; `false` keeps only those that have
   * not. The endpoint treats the omitted parameter as "all", so an unset value
   * must NOT send the query parameter at all — sending `false` silently drops
   * every clinic that has a report.
   */
  hasQbPublished?: boolean | null;
}

/** Returns the number of criteria that are actively set. */
export function aysClinicSearchCriteriaActiveCount(
  criteria: AysClinicSearchCriteria,
): number {
  return [
    criteria.bundesland != null,
    criteria.traeger != null,
    !!criteria.years?.length,
    criteria.onlyLive != null,
    criteria.hasQbPublished != null,
  ].filter(Boolean).length;
}
